import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Settings, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import type { MetricRequest, RankItem } from '@contracts';
import { fetchMetric } from '@/services/metricService';
import { useFilters } from '@/lib/filterContext';
import { CountUp } from '@/components/CountUp';
import { RaceGoalsSettings } from '@/components/RaceGoalsSettings';
import { loadGoals, saveGoals, periodToGoalPeriod, type GoalConfig } from '@/components/raceGoals';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 });
const moneyShort = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

const LANE_COLORS = ['#dc2626', '#2563eb', '#0891b2', '#16a34a', '#7c3aed', '#d97706', '#db2777'];
const CONFETTI = ['#facc15', '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#ec4899'];
const MEDAL = ['🥇', '🥈', '🥉'];
// Piezas de ajedrez VARIADAS por lugar (fallback de SB hasta que haya imágenes oficiales).
const CHESS = ['♚', '♛', '♜', '♝', '♞', '♟', '♟'];

// ── Registry de escenarios (data-driven). Agregar uno = una entrada + su imagen ──
interface RaceScenario {
  scene: string;
  topN: number;
  lanes: number[];     // centro Y de cada carril (% del scene, medido)
  runStart: number;    // x% de salida
  runFinish: number;   // x% de la meta
  scoreboardX: number; // x% del marcador
  scoreboardY?: number[]; // Y de cada slot del marcador (si difiere de los carriles)
  scoreboardPad?: string;        // padding de la pastilla CON medalla (top 3), en cqw
  scoreboardPadNoMedal?: string; // padding de la pastilla SIN medalla (4°+): más horizontal para igualar ancho
  scoreboardH?: string;          // alto MÍNIMO de la pastilla (uniforme con/sin medalla), en cqw
  racerH: number;      // alto del corredor (% del alto del scene)
  /** Imagen OFICIAL del corredor por lugar (si existe se usa; si no, cae al fallback). */
  racerImg: (rank: number) => string;
  /** Fallback (emoji/glyph) cuando la imagen no está. */
  racerFallback: (rank: number) => string;
  /** Fondo de la vista MÓVIL (barras) por escenario. */
  mobileBg: string;       // imagen de fondo (cover)
  mobileBgColor: string;  // color sólido de respaldo si la imagen no carga
  mobileOverlay: string;  // velo (linear-gradient) sobre el fondo, para contraste del texto
}

const SCENARIOS: Record<'vg' | 'sb', RaceScenario> = {
  vg: {
    scene: '/race/scene.png',
    topN: 7,
    lanes: [12, 26.5, 39, 51.5, 64, 76.5, 90.2],
    runStart: 7,
    runFinish: 82,
    scoreboardX: 94.5,
    racerH: 10,
    racerImg: () => '/race/horse.png',
    racerFallback: () => '🐎',
    mobileBg: '/race/grass.png',
    mobileBgColor: '#143218',
    mobileOverlay: 'linear-gradient(rgba(10,30,14,0.62), rgba(10,30,14,0.80))',
  },
  sb: {
    scene: '/race/scene-sb.png',
    topN: 7,
    lanes: [10.6, 23.7, 36.9, 50, 63.1, 76.3, 89.4],
    runStart: 6,
    runFinish: 80,
    scoreboardX: 92,
    scoreboardY: [14, 26.9, 39.8, 51.7, 63.6, 76.5, 88.4], // centro de cada óvalo (ajustado por el user en dev tools)
    scoreboardPad: '1.35cqw 0.9cqw',        // con medalla (top 3)
    scoreboardPadNoMedal: '1.35cqw 2.3cqw', // sin medalla (4°+): + horizontal para igualar ancho
    scoreboardH: '4.6cqw',                  // alto uniforme con/sin medalla
    racerH: 11,
    racerImg: (r) => `/race/sb-piece-${r + 1}.png`,
    racerFallback: (r) => CHESS[r % CHESS.length],
    mobileBg: '/race/chess.png',
    mobileBgColor: '#6b5536',
    // Madera clara → velo cálido y ligero (que se vea el tablero), el texto lleva sombra.
    mobileOverlay: 'linear-gradient(rgba(40,28,16,0.18), rgba(40,28,16,0.40))',
  },
};

interface Runner { name: string; value: number; orders: number; color: string; frac: number }

export interface DailyRaceModalProps { open: boolean; onClose: () => void }

/** Corredor: imagen oficial del escenario si existe; si no, el fallback (🐎 / pieza de ajedrez).
 *  `heightPx` fija el alto en px (vista móvil en barras); por default escala con la pista (cqh). */
function Racer({ sc, rank, heightPx }: { sc: RaceScenario; rank: number; heightPx?: number }) {
  const [broken, setBroken] = useState(false);
  const img = sc.racerImg(rank);
  const h = heightPx ? `${heightPx}px` : `${sc.racerH}cqh`;
  if (!broken) {
    return (
      <img
        src={img}
        alt=""
        draggable={false}
        onError={() => setBroken(true)}
        style={{ height: h }}
        className="w-auto"
      />
    );
  }
  return (
    <span style={{ fontSize: heightPx ? `${heightPx}px` : `${sc.racerH * 0.95}cqh`, lineHeight: 1, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.45))' }}>
      {sc.racerFallback(rank)}
    </span>
  );
}

/** Carrera del Día. Escenario por empresa (VG = caballos; SmartBamboo = ajedrez). El
 *  front solo PINTA: corredores ∝ ventas (branchesRanking), reconocimiento al ganador. */
export function DailyRaceModal({ open, onClose }: DailyRaceModalProps) {
  const { companies, scope, period } = useFilters();
  const [phase, setPhase] = useState<'gate' | 'racing' | 'finished'>('gate');
  const [showWinner, setShowWinner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const sbOnly = companies.length > 0 && companies.every((c) => c === 'sb');
  const sc = sbOnly ? SCENARIOS.sb : SCENARIOS.vg;
  const scKey = sbOnly ? 'sb' : 'vg';
  const raceCompanies = sbOnly ? ['sb'] : companies.filter((c) => c !== 'sb');

  // Modo Objetivos: config por escenario (localStorage). Recarga al cambiar de escenario.
  const [goalCfg, setGoalCfg] = useState<GoalConfig>(() => loadGoals(scKey));
  useEffect(() => { setGoalCfg(loadGoals(scKey)); }, [scKey]);
  const goal = goalCfg.goals[periodToGoalPeriod(period)];
  const goalOn = goalCfg.enabled && goal > 0;

  // unscopedBranches: la carrera muestra TODAS las plazas de las compañías permitidas
  // (Top-7 completo) aunque el usuario sea gerente de 1 sola sucursal. El BFF mantiene
  // el scope de compañía; solo branchesRanking honra este flag.
  const req: MetricRequest = { metric: 'branchesRanking', view: 'ventas', period, companies: raceCompanies, scope, unscopedBranches: true };
  const { data, isLoading } = useQuery({
    queryKey: ['race', raceCompanies, scope, period],
    queryFn: () => fetchMetric(req),
    enabled: open && raceCompanies.length > 0,
  });

  const runners: Runner[] = useMemo(() => {
    const items = ((data?.data as RankItem[] | undefined) ?? []).slice(0, sc.topN);
    // Con Objetivos: la meta es el divisor (quien la cumple llega a la meta). Sin él: relativo al líder.
    const denom = goalOn ? goal : Math.max(1, ...items.map((i) => i.value));
    return items.map((i, idx) => ({
      name: i.name, value: i.value, orders: i.orders, color: LANE_COLORS[idx % LANE_COLORS.length],
      frac: Math.min(1, i.value / denom),
    }));
  }, [data, sc.topN, goalOn, goal]);

  useEffect(() => {
    setPhase('gate');
    setShowWinner(false);
    if (!open || !runners.length) return;
    const t1 = setTimeout(() => setPhase('racing'), 700);
    const t2 = setTimeout(() => setPhase('finished'), 700 + 3700);
    const t3 = setTimeout(() => { if (runners[0]?.value > 0) setShowWinner(true); }, 700 + 3700 + 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [open, runners]);

  if (!open) return null;
  const moving = phase !== 'gate';
  const winner = runners[0];
  const hasSales = !!winner && winner.value > 0;
  const baseSubtitle = sbOnly ? 'Top 7 sucursales · SmartBamboo' : 'Top 7 sucursales · Vine Garden / Piélé';
  const subtitle = goalOn ? `${baseSubtitle} · 🎯 Meta ${moneyShort(goal)}` : baseSubtitle;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="relative flex max-h-[94dvh] w-full min-w-0 max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-center gap-3 bg-gradient-to-r from-[#7d1818] via-[#9d1d1d] to-[#c0392b] px-5 py-3 text-white">
          <span className="text-2xl">{sbOnly ? '♟️' : '🏇'}</span>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-black leading-tight">Carrera del Día</h2>
            <p className="truncate text-xs text-white/80">{subtitle}</p>
          </div>
          <button
            onClick={() => setShowSettings(true)}
            title="Modo Objetivos"
            className={`rounded-full p-1.5 transition-colors ${goalOn ? 'bg-emerald-400/90 text-white' : 'bg-white/15 text-white/80 hover:bg-white/25'}`}
          >
            <Settings className="h-4 w-4" />
          </button>
          <button onClick={onClose} title="Cerrar" className="rounded-full bg-white/15 p-1.5 text-white/80 hover:bg-white/25">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* PISTA (desktop): escenario landscape. En móvil no cabe → se usa la vista en barras. */}
        <div className="relative hidden w-full select-none sm:block" style={{ aspectRatio: '1313 / 800', containerType: 'size' }}>
          <img src={sc.scene} alt="" draggable={false} className="absolute inset-0 h-full w-full" />

          {(isLoading || !runners.length) && (
            <div className="absolute inset-x-[12%] top-[46%] text-center text-sm font-semibold text-white drop-shadow">
              {isLoading ? 'Preparando la carrera…' : 'Sin ventas en el período seleccionado.'}
            </div>
          )}

          {/* Corredores + plecas */}
          {runners.map((r, idx) => {
            const x = moving ? sc.runStart + r.frac * (sc.runFinish - sc.runStart) : sc.runStart;
            return (
              <div
                key={r.name + idx}
                className="absolute -translate-y-1/2 transition-[left] ease-out"
                style={{ left: `${x}%`, top: `${sc.lanes[idx]}%`, transitionDuration: `${2800 + idx * 110}ms` }}
              >
                <div className="flex -translate-x-1/2 flex-col items-center">
                  <span
                    className="whitespace-nowrap rounded-full font-bold leading-tight text-white shadow"
                    style={{ backgroundColor: r.color, fontSize: 'max(9px, 1.1cqw)', padding: '0.2cqw 0.7cqw', marginBottom: '0.3cqw' }}
                  >
                    {idx + 1}° {r.name}
                  </span>
                  <Racer sc={sc} rank={idx} />
                </div>
              </div>
            );
          })}

          {/* Marcador: pastilla blanca + medalla (top 3) + monto */}
          {runners.map((r, i) => {
            // Sin medalla (4°+) lleva más padding horizontal para igualar el ancho del top 3.
            const pad = MEDAL[i]
              ? sc.scoreboardPad ?? '0.35cqw 0.9cqw'
              : sc.scoreboardPadNoMedal ?? sc.scoreboardPad ?? '0.35cqw 0.9cqw';
            return (
              <div
                key={`sb${i}`}
                className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/5"
                style={{ left: `${sc.scoreboardX}%`, top: `${(sc.scoreboardY ?? sc.lanes)[i]}%`, gap: '0.4cqw', padding: pad, minHeight: sc.scoreboardH }}
              >
                {MEDAL[i] && <span style={{ fontSize: 'max(12px, 1.5cqw)', lineHeight: 1 }}>{MEDAL[i]}</span>}
                <span className="font-bold tabular-nums text-slate-800" style={{ fontSize: 'max(9px, 0.95cqw)' }}>
                  {moneyShort(r.value)}
                </span>
              </div>
            );
          })}

        </div>

        {/* MÓVIL: carrera en barras verticales (la pista landscape no cabe en teléfono). Fondo
            POR ESCENARIO (pasto VG / ajedrez SB) con velo para contraste; color sólido de
            respaldo si la imagen no carga. */}
        <div
          className="px-3 py-3 sm:hidden"
          style={{
            backgroundColor: sc.mobileBgColor,
            backgroundImage: `${sc.mobileOverlay}, url(${sc.mobileBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {isLoading || !runners.length ? (
            <p className="py-8 text-center text-sm font-semibold text-white/90">
              {isLoading ? 'Preparando la carrera…' : 'Sin ventas en el período seleccionado.'}
            </p>
          ) : (
            <div className="space-y-2.5">
              {runners.map((r, idx) => {
                const pct = (moving ? r.frac : 0) * 100;
                const dur = `${2800 + idx * 110}ms`;
                return (
                  <div key={r.name + idx}>
                    <div
                      className="mb-1 flex items-center justify-between gap-2 text-xs text-white"
                      style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85)' }}
                    >
                      <span className="flex min-w-0 items-center gap-1.5 font-semibold">
                        <span className="shrink-0">{MEDAL[idx] ?? `${idx + 1}°`}</span>
                        <span className="truncate">{r.name}</span>
                      </span>
                      <span className="shrink-0 tabular-nums font-bold">{moneyShort(r.value)}</span>
                    </div>
                    <div className="relative h-6 overflow-hidden rounded-full bg-black/30 ring-1 ring-white/10">
                      <div className="pointer-events-none absolute inset-y-0 right-1.5 z-10 flex items-center text-[11px] opacity-80">🏁</div>
                      <div
                        className="h-full rounded-full transition-[width] ease-out"
                        style={{ width: `${pct}%`, backgroundColor: r.color, transitionDuration: dur }}
                      />
                      <span
                        className="absolute top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 transition-[left] ease-out"
                        style={{ left: `${pct}%`, transitionDuration: dur, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.5))' }}
                      >
                        <Racer sc={sc} rank={idx} heightPx={34} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Reconocimiento al ganador (cubre el modal completo) */}
        <WinnerOverlay show={showWinner && hasSales} winner={winner} onClose={() => setShowWinner(false)} onDash={onClose} />

        {/* FOOTER */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-100 bg-white px-4 py-3 sm:px-5">
          <span className="text-2xl">🏆</span>
          <div className="min-w-0 flex-1 text-sm">
            {hasSales ? (
              <span>
                <strong style={{ color: winner.color }}>{winner.name}</strong> {goalOn ? 'lidera con' : 'gana la carrera con'}{' '}
                <strong>{money(winner.value)}</strong>
                {goalOn && <span className="text-slate-500"> · {Math.round((winner.value / goal) * 100)}% de la meta</span>}
              </span>
            ) : (
              <span className="text-slate-500">Sin ventas en el período — la carrera arranca con los primeros tickets.</span>
            )}
          </div>
          <button onClick={onClose} className="ml-auto shrink-0 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">
            Ir al dashboard
          </button>
        </div>

        {showSettings && (
          <RaceGoalsSettings
            initial={goalCfg}
            onCancel={() => setShowSettings(false)}
            onSave={(cfg) => { saveGoals(scKey, cfg); setGoalCfg(cfg); setShowSettings(false); }}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

function WinnerOverlay({ show, winner, onClose, onDash }: {
  show: boolean; winner?: Runner; onClose: () => void; onDash: () => void;
}): ReactNode {
  return (
    <AnimatePresence>
      {show && winner && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
          className="absolute inset-0 z-30 flex items-center justify-center p-6"
          style={{ background: 'radial-gradient(circle at center, rgba(15,15,20,0.78), rgba(15,15,20,0.92))', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
          onClick={onClose}
        >
          {Array.from({ length: 28 }).map((_, i) => {
            const color = CONFETTI[i % CONFETTI.length];
            const dx = (Math.random() - 0.5) * 600;
            const dy = (Math.random() - 0.5) * 400 - 50;
            const rot = (Math.random() - 0.5) * 720;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.5, x: 0, y: 0, rotate: 0 }}
                animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1, 1, 0.8], x: dx, y: dy, rotate: rot }}
                transition={{ duration: 2.5 + Math.random() * 1.5, delay: Math.random() * 0.5, repeat: Infinity, repeatDelay: 0.5, ease: 'easeOut' }}
                className="absolute rounded-sm"
                style={{ width: 8 + Math.random() * 8, height: 12 + Math.random() * 8, background: color, boxShadow: `0 2px 8px ${color}aa` }}
              />
            );
          })}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 40, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 20 }}
            transition={{ type: 'spring', stiffness: 230, damping: 18, delay: 0.15 }}
            className="relative w-full max-w-sm rounded-3xl px-10 py-7 text-center"
            style={{ background: 'linear-gradient(135deg, #fef3c7 0%, white 50%, #fee2e2 100%)', boxShadow: '0 30px 80px rgba(0,0,0,0.5), 0 0 0 4px rgba(250,204,21,0.6), 0 0 100px rgba(250,204,21,0.5)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.span
              animate={{ rotate: [-3, 3, -3], y: [-2, 2, -2] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-block text-7xl"
              style={{ filter: 'drop-shadow(0 0 20px rgba(250,204,21,0.7))' }}
            >
              🏆
            </motion.span>
            <div className="mt-2">
              <span
                className="inline-block rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-white"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #b45309)', boxShadow: '0 4px 12px rgba(245,158,11,0.4)' }}
              >
                🥇 Ganador
              </span>
            </div>
            <h2
              className="mt-3 text-2xl font-black leading-tight tracking-tight sm:text-3xl"
              style={{ background: 'linear-gradient(135deg, #9d1d1d, #c0392b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
            >
              {winner.name}
            </h2>
            <div className="mt-3">
              <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Total de ventas</span>
              <div className="mt-1 text-4xl font-black tabular-nums leading-none text-gray-900 sm:text-5xl">
                <CountUp value={winner.value} format={money} />
              </div>
              <span className="mt-1.5 block text-xs font-medium text-gray-500">
                {winner.orders} {winner.orders === 1 ? 'orden' : 'órdenes'}
              </span>
            </div>
            <button onClick={onDash} className="mt-5 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">
              Ir al dashboard
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
